import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatPhoneNumber } from "../../utils/strings";

import { AgentEnvironment, agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";
import { AgentEnvironmentVariable, agentEnvironmentVariableActions } from "../../redux/reducers/agentEnvironmentVariableSlice";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DropdownField,
  FieldLabel,
  InputField,
  SwitchField,
  Text,
  Tooltip,
  View,
} from "../../components/base";
import { organizationPhoneNumberActions } from "../../redux/reducers/organizationPhoneNumberSlice";
import { organizationTelephonyActions } from "../../redux/reducers/organizationTelephonySlice";
import { PhoneNumberAssignmentsDialog } from "../../components/agents/PhoneNumberAssignmentsDialog";
import { CloneAgentEnvironmentDialog } from "../../components/agents/CloneAgentEnvironmentDialog";
import { CreateAgentEnvironmentDialog } from "../../components/agents/CreateAgentEnvironmentDialog";
import { EmptyState } from "../../components/base/EmptyState";
import { ErrorFallbackSettingsDialog } from "../../components/agents/ErrorFallbackSettingsDialog";
import { featureActions } from "../../redux/reducers/featureSlice";
import { getGradientForName } from "../../utils/gradients";
import { Icon } from "../../components/base/Icon";
import { PrivacySettingsDialog } from "../../components/agents/PrivacySettingsDialog";
import { RegionSettingsDialog } from "../../components/agents/RegionSettingsDialog";
import { StartupSettingsDialog } from "../../components/agents/StartupSettingsDialog";
import { agentFunctionActions } from "../../redux/reducers/agentFunctionSlice";
import { useDebouncedCallback } from "use-debounce";
import { useAuth } from "../../hooks/useAuth";
import { STTSettingsDialog } from "../../components/agents/STTSettingsDialog";
import { AllowedOriginsSettingsDialog } from "../../components/agents/AllowedOriginsSettingsDialog";
import { WebhookSettingsDialog } from "../../components/agents/WebhookSettingsDialog";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { useSearchParam } from "../../hooks/useSearchParam";
import { VariablesForm } from "../../components/VariablesForm";
import { useSTTProviders } from "../../hooks/useSTTProviders";
import { isSOC2Attested } from "../../utils/soc2";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.2 },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

export const AgentEnvironmentsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const { isAdmin } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [cloneEnvironment, setCloneEnvironment] = useState<AgentEnvironment | null>(null);
  const [showSTTSettingsDialog, setShowSTTSettingsDialog] = useState(false);
  const [showErrorFallbackSettingsDialog, setShowErrorFallbackSettingsDialog] = useState(false);
  const [showStartupSettingsDialog, setShowStartupSettingsDialog] = useState(false);
  const [showPrivacySettingsDialog, setShowPrivacySettingsDialog] = useState(false);
  const [showRegionSettingsDialog, setShowRegionSettingsDialog] = useState(false);
  const [showWebhookSettingsDialog, setShowWebhookSettingsDialog] = useState(false);
  const [showAllowedOriginsSettingsDialog, setShowAllowedOriginsSettingsDialog] = useState(false);
  const [showPhoneAssignmentsDialog, setShowPhoneAssignmentsDialog] = useState(false);
  const environmentId = useSearchParam("e");
  const openModal = useSearchParam("openModal");
  const agentEnvironmentsFormData = useAppSelector((state) => state.feature.agentEnvironmentsFormData);
  const agentEnvironmentVariables = useAppSelector((state) =>
    Object.values(state.agentEnvironmentVariable.entities).filter((v) => v.agentEnvironmentId === environmentId),
  );
  const environments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === agentId));
  const currentEnvironment = useAppSelector((state) => state.agentEnvironment.entities[environmentId]);
  const agentFunctions = useAppSelector((state) => Object.values(state.agentFunction.entities).filter((func) => func.agentId === agentId));
  const { organizationId } = useEffectiveOrganization();
  const { getAllModels, getProviderForModel } = useSTTProviders();
  const functionOptions = agentFunctions.map((f) => ({
    label: <Text className="text-gray-900 dark:text-gray-100">{f.name || `Function ${f.id.substring(0, 8)}...`}</Text>,
    value: f.id,
  }));

  const orgPhoneNumbers = useAppSelector((state) => Object.values(state.organizationPhoneNumbers.entities));

  const onChangeEnvironmentHandler = (key: string) => (value: unknown) => {
    if (key === "sttEndpointing") {
      value = isNaN(Number(value)) ? 300 : Number(value);
    }
    if (key === "ringingEnabled") {
      value = String(value) === "true";
    }
    if (key === "recordingEnabled") {
      value = String(value) === "true";
    }

    dispatch(
      featureActions.setAgentEnvironmentsFormData({
        ...agentEnvironmentsFormData,
        [key]: value,
      }),
    );
  };

  const onChangeEnvironmentVariable = useDebouncedCallback((updatedVariable: Partial<AgentEnvironmentVariable>) => {
    dispatch(
      agentEnvironmentVariableActions.updateById({
        ...updatedVariable,
        agentId,
        agentEnvironmentId: environmentId,
      }),
    );
  }, 1000);

  const onCreateEnvironmentVariable = (newVariable: { name: string; value: string; masked: boolean }) => {
    dispatch(
      agentEnvironmentVariableActions.create({
        ...newVariable,
        agentId,
        agentEnvironmentId: environmentId,
      }),
    );
  };

  const onClickDeleteEnvironmentVariable = async (id: string) => {
    await dispatch(
      agentEnvironmentVariableActions.deleteById({
        agentId,
        agentEnvironmentId: environmentId,
        id,
      }),
    );
  };

  useEffect(() => {
    if (currentEnvironment) {
      dispatch(featureActions.setAgentEnvironmentsFormData(currentEnvironment));
      dispatch(agentEnvironmentVariableActions.get({ agentId: agentId || "", agentEnvironmentId: environmentId }));
    }
  }, [currentEnvironment]);

  // Handle opening modals from URL param (used by command palette)
  useEffect(() => {
    if (!openModal) return;

    // If no environment selected but environments exist, auto-select first one
    if (!environmentId && environments.length > 0) {
      navigate(`/agents/${agentId}/environments?openModal=${openModal}&e=${environments[0].id}`, { replace: true });
      return;
    }

    // Open the modal once environment is loaded
    if (currentEnvironment) {
      // Close all modals first
      setShowSTTSettingsDialog(false);
      setShowStartupSettingsDialog(false);
      setShowErrorFallbackSettingsDialog(false);
      setShowPrivacySettingsDialog(false);
      setShowWebhookSettingsDialog(false);
      setShowAllowedOriginsSettingsDialog(false);
      setShowRegionSettingsDialog(false);
      setShowPhoneAssignmentsDialog(false);

      // Open the requested modal
      switch (openModal) {
        case "stt":
          setShowSTTSettingsDialog(true);
          break;
        case "startup":
          setShowStartupSettingsDialog(true);
          break;
        case "fallback":
          setShowErrorFallbackSettingsDialog(true);
          break;
        case "privacy":
          setShowPrivacySettingsDialog(true);
          break;
        case "webhook":
          setShowWebhookSettingsDialog(true);
          break;
        case "allowed-origins":
          setShowAllowedOriginsSettingsDialog(true);
          break;
        case "region":
          setShowRegionSettingsDialog(true);
          break;
        case "phone":
          setShowPhoneAssignmentsDialog(true);
          break;
      }
      // Clear the openModal param from URL after opening
      navigate(`/agents/${agentId}/environments?e=${environmentId}`, { replace: true });
    }
  }, [openModal, currentEnvironment, environments, agentId, environmentId, navigate]);

  useEffect(() => {
    if (agentId) {
      dispatch(agentFunctionActions.get({ agentId } as any) as any);
    }
  }, [agentId, dispatch]);

  useEffect(() => {
    if (organizationId) {
      dispatch(organizationPhoneNumberActions.get({}) as any);
      dispatch(organizationTelephonyActions.get({}) as any);
    }
  }, [organizationId, dispatch]);

  // Derive phone assignments per environment directly from Redux
  const phoneNumbersByEnv = orgPhoneNumbers.reduce<Record<string, typeof orgPhoneNumbers>>((acc, pn) => {
    if (pn.agentEnvironmentId) {
      if (!acc[pn.agentEnvironmentId]) acc[pn.agentEnvironmentId] = [];
      acc[pn.agentEnvironmentId].push(pn);
    }
    return acc;
  }, {});

  const gradient = getGradientForName(agentEnvironmentsFormData?.name || "");
  const currentDeployedFunction = agentFunctions.find((func) => func.id === currentEnvironment?.functionId);

  return (
    <View className="flex flex-col flex-1 p-4 overflow-hidden min-w-0">
      {environments.length === 0 ? (
        <EmptyState
          icon="grid"
          title="No Environments Found"
          description="Create your first environment to get started. Environments help you manage different configurations for your agent."
          actionLabel="Create Your First Environment"
          onAction={() => setShowCreateDialog(true)}
        />
      ) : environmentId ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="flex flex-col flex-1 space-y-4 overflow-hidden"
        >
          <View className="flex flex-col flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700">
            <View className="flex items-center justify-between min-h-[60px] p-2 border-b border-gray-200 dark:border-gray-700 overflow-y-auto">
              <motion.div
                initial={{ x: -60, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="flex items-center space-x-2 min-h-11"
              >
                <button
                  onClick={() => navigate(`/agents/${agentId}/environments`)}
                  type="button"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon
                    name="close"
                    size={18}
                    className="text-gray-500 dark:text-gray-400"
                  />
                </button>
                {agentEnvironmentsFormData?.name && (
                  <View className="flex items-center gap-3">
                    <View
                      className={`h-9 w-9 rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} ${gradient.darkFrom} ${gradient.darkTo} flex items-center justify-center`}
                    >
                      <Text className="text-xs font-bold text-white">{agentEnvironmentsFormData.name.substring(0, 2).toUpperCase()}</Text>
                    </View>
                    <View className="flex flex-col">
                      <Text className="text-lg font-semibold text-gray-900 dark:text-white">{agentEnvironmentsFormData.name}</Text>
                      <View className="flex items-center gap-2">
                        <Text className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {agentEnvironmentsFormData.name.toLowerCase().replace(/\s+/g, "-")}
                        </Text>
                        {currentDeployedFunction && (
                          <Text className="text-xs font-medium text-indigo-500 dark:text-indigo-400">Function: {currentDeployedFunction.name}</Text>
                        )}
                        {phoneNumbersByEnv[environmentId]?.length > 0 && (
                          <View className="flex items-center gap-1.5">
                            {phoneNumbersByEnv[environmentId].map((pn: any) => (
                              <Text
                                key={pn.id}
                                className="text-xs font-medium text-green-500 dark:text-green-400"
                              >
                                {formatPhoneNumber(pn.phoneNumber)}
                              </Text>
                            ))}
                          </View>
                        )}
                      </View>
                    </View>
                  </View>
                )}
              </motion.div>
              <View className="flex items-center gap-1">
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowPhoneAssignmentsDialog(true)}
                >
                  <Icon
                    name="phone"
                    size={14}
                  />
                  Phone
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowStartupSettingsDialog(true)}
                >
                  <Icon
                    name="volume-up"
                    size={14}
                  />
                  Startup
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowSTTSettingsDialog(true)}
                >
                  <Icon
                    name="settings"
                    size={14}
                  />
                  STT
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowErrorFallbackSettingsDialog(true)}
                >
                  <Icon
                    name="info"
                    size={14}
                  />
                  Failover
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowPrivacySettingsDialog(true)}
                >
                  <Icon
                    name="shield"
                    size={14}
                  />
                  Privacy
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowRegionSettingsDialog(true)}
                >
                  <Icon
                    name="language"
                    size={14}
                  />
                  Region
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowWebhookSettingsDialog(true)}
                >
                  <Icon
                    name="link"
                    size={14}
                  />
                  Webhook
                </Button>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setShowAllowedOriginsSettingsDialog(true)}
                >
                  <Icon
                    name="lock"
                    size={14}
                  />
                  CORS
                </Button>
                {(() => {
                  const sttModel = agentEnvironmentsFormData?.sttModel;
                  const sttProvider = sttModel ? getProviderForModel(sttModel) : undefined;
                  const sttAttested = sttProvider ? (sttProvider.soc2Attested ?? isSOC2Attested(sttProvider.id)) : true;

                  const tooltipContent = sttAttested ? "STT is SOC 2 Attested" : "STT is not SOC 2 Attested";

                  return (
                    <Tooltip content={tooltipContent}>
                      <View
                        className={`flex flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium ${
                          sttAttested ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400"
                        }`}
                      >
                        <Icon
                          name="shield"
                          size={14}
                        />
                        SOC 2{!sttAttested && " ⚠"}
                      </View>
                    </Tooltip>
                  );
                })()}
              </View>
            </View>
            {phoneNumbersByEnv[environmentId]?.length > 0 && !currentDeployedFunction && (
              <Alert
                variant="warning"
                className="mx-4 mt-4"
              >
                <AlertTitle className="text-sm">Phone number configured without a function.</AlertTitle>
                <AlertDescription className="text-sm">Calls will fail until a function is deployed to this environment.</AlertDescription>
              </Alert>
            )}
            <View className="flex flex-col flex-1 p-4 space-y-4 overflow-y-auto">
              <InputField
                labelClassName="!w-full"
                className="!w-full"
                label="Name"
                value={agentEnvironmentsFormData?.name || ""}
                onChange={onChangeEnvironmentHandler("name")}
              />
              <FieldLabel
                label="Environment Variables"
                className="w-full flex flex-col flex-1"
              >
                <VariablesForm
                  variables={agentEnvironmentVariables}
                  onChange={onChangeEnvironmentVariable}
                  onCreate={onCreateEnvironmentVariable}
                  onClickDelete={onClickDeleteEnvironmentVariable}
                />
              </FieldLabel>
            </View>
          </View>
        </motion.div>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-4 w-full"
        >
          {environments.map((environment) => {
            const gradient = getGradientForName(environment.name);
            const deployedFunction = agentFunctions.find((func) => func.id === environment.functionId);
            return (
              <motion.div
                key={environment.id}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="w-full px-4 py-2 flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700"
              >
                <View className="flex items-center gap-3">
                  <View
                    className={`h-9 w-9 rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} ${gradient.darkFrom} ${gradient.darkTo} flex items-center justify-center`}
                  >
                    <Text className="text-xs font-bold text-white">{environment.name.substring(0, 2).toUpperCase()}</Text>
                  </View>
                  <View className="flex flex-col">
                    <Text className="text-lg font-semibold text-gray-900 dark:text-white">{environment.name}</Text>
                    <View className="flex items-center gap-2">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">ID: {environment?.name?.toLowerCase().replace(/\s+/g, "-")}</Text>
                      {deployedFunction && (
                        <Text className="text-xs font-medium text-indigo-500 dark:text-indigo-400">Function: {deployedFunction.displayName}</Text>
                      )}
                      {phoneNumbersByEnv[environment.id]?.length > 0 && !deployedFunction && (
                        <View className="flex items-center gap-1 text-warning-dark dark:text-warning-light">
                          <Icon
                            name="warning-amber"
                            size={14}
                          />
                          <Text className="text-xs font-medium">Calls will fail – no function</Text>
                        </View>
                      )}
                      {phoneNumbersByEnv[environment.id]?.length > 0 && (
                        <View className="flex items-center gap-1.5">
                          <Icon
                            name="phone"
                            size={12}
                            className="text-green-500 dark:text-green-400"
                          />
                          {phoneNumbersByEnv[environment.id].map((pn: any) => (
                            <Text
                              key={pn.id}
                              className="text-xs font-medium text-green-500 dark:text-green-400"
                            >
                              {formatPhoneNumber(pn.phoneNumber)}
                            </Text>
                          ))}
                        </View>
                      )}
                      <View className="flex items-center gap-1">
                        <Icon
                          name={environment.recordingEnabled ? "mic" : "mic-off"}
                          size={14}
                          className={environment.recordingEnabled ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}
                        />
                        <Text className="text-xs font-medium text-gray-600 dark:text-gray-300">Recording {environment.recordingEnabled ? "On" : "Off"}</Text>
                      </View>
                      <View className="flex items-center gap-1">
                        <Icon
                          name={environment.redactionEnabled ? "lock" : "unlock"}
                          size={14}
                          className={environment.redactionEnabled ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}
                        />
                        <Text className="text-xs font-medium text-gray-600 dark:text-gray-300">Redaction {environment.redactionEnabled ? "On" : "Off"}</Text>
                      </View>
                      <View className="flex items-center gap-1">
                        <Icon
                          name="activity"
                          size={14}
                          className={environment.tracingEnabled ? "text-indigo-500 dark:text-indigo-400" : "text-gray-400 dark:text-gray-500"}
                        />
                        <Text className="text-xs font-medium text-gray-600 dark:text-gray-300">Tracing {environment.tracingEnabled ? "On" : "Off"}</Text>
                      </View>
                    </View>
                  </View>
                </View>
                <View className="flex items-center gap-2">
                  <Button
                    variant="neutral"
                    size="sm"
                    outline
                    onClick={() => setCloneEnvironment(environment)}
                  >
                    <Icon
                      name="copy"
                      size={14}
                    />
                    Clone
                  </Button>
                  <Button
                    variant="neutral"
                    size="sm"
                    outline
                    onClick={() => navigate(`/agents/${agentId}/environments?e=${environment.id}`)}
                  >
                    <Icon
                      name="edit"
                      size={14}
                    />
                    Edit
                  </Button>
                </View>
              </motion.div>
            );
          })}
        </motion.div>
      )}
      <CreateAgentEnvironmentDialog
        agentId={agentId || ""}
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />
      <CloneAgentEnvironmentDialog
        agentId={agentId || ""}
        sourceEnvironment={cloneEnvironment}
        isOpen={!!cloneEnvironment}
        onClose={() => setCloneEnvironment(null)}
      />
      <STTSettingsDialog
        isOpen={showSTTSettingsDialog}
        onClose={() => setShowSTTSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onSave={(config) => {
          dispatch(featureActions.setAgentEnvironmentsFormData({ ...agentEnvironmentsFormData, ...config }));
          if (agentEnvironmentsFormData.id && agentId) {
            dispatch(
              agentEnvironmentActions.updateById({
                id: agentEnvironmentsFormData.id,
                agentId,
                ...config,
                sttFilter: config.sttFilter ?? undefined,
                deploymentAction: "config",
              }),
            );
          }
        }}
        isAdmin={isAdmin}
      />
      <ErrorFallbackSettingsDialog
        isOpen={showErrorFallbackSettingsDialog}
        onClose={() => setShowErrorFallbackSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onChange={onChangeEnvironmentHandler}
      />
      <StartupSettingsDialog
        isOpen={showStartupSettingsDialog}
        onClose={() => setShowStartupSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onSave={(config) => {
          dispatch(featureActions.setAgentEnvironmentsFormData({ ...agentEnvironmentsFormData, ...config }));
          if (agentEnvironmentsFormData.id && agentId) {
            dispatch(
              agentEnvironmentActions.updateById({
                id: agentEnvironmentsFormData.id,
                agentId,
                ...config,
                deploymentAction: "config",
              }),
            );
          }
        }}
        organizationId={organizationId || ""}
      />
      <PrivacySettingsDialog
        isOpen={showPrivacySettingsDialog}
        onClose={() => setShowPrivacySettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onSave={(config) => {
          dispatch(featureActions.setAgentEnvironmentsFormData({ ...agentEnvironmentsFormData, ...config }));
          if (agentEnvironmentsFormData.id && agentId) {
            dispatch(
              agentEnvironmentActions.updateById({
                id: agentEnvironmentsFormData.id,
                agentId,
                ...config,
                deploymentAction: "config",
              }),
            );
          }
        }}
        organizationId={organizationId || ""}
        isAdmin={isAdmin}
      />
      <RegionSettingsDialog
        isOpen={showRegionSettingsDialog}
        onClose={() => setShowRegionSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onChange={onChangeEnvironmentHandler}
      />
      <WebhookSettingsDialog
        isOpen={showWebhookSettingsDialog}
        onClose={() => setShowWebhookSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onChange={onChangeEnvironmentHandler}
      />
      <AllowedOriginsSettingsDialog
        isOpen={showAllowedOriginsSettingsDialog}
        onClose={() => setShowAllowedOriginsSettingsDialog(false)}
        config={agentEnvironmentsFormData}
        onChange={onChangeEnvironmentHandler}
      />
      <PhoneNumberAssignmentsDialog
        isOpen={showPhoneAssignmentsDialog}
        onClose={() => setShowPhoneAssignmentsDialog(false)}
        agentId={agentId || ""}
        environmentId={environmentId || ""}
      />
    </View>
  );
};
